import React, { useEffect, useState } from "react";
import { Box, Divider, Popover, Typography } from "@mui/material";
import { DragOverlay, useDraggable } from "@dnd-kit/core";
import { getCloudStorageProviders } from "../../../appStore/actions/cloudStorage/action";
import { CHAT_SHORTCUTS, getChatShortcutMeta } from "../../../utils/chatShortcuts";
import googleDriveIcon from "../../../assets/images/chats/googledrive.svg";
import oneDriveIcon from "../../../assets/images/chats/onedrive_svg.svg";
import removeIcon from "../../../assets/images/chats/remove.svg";

const iconBoxStyle = {
  width: "32px",
  height: "32px",
  minWidth: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid rgba(0, 0, 0, 0.15)",
  borderRadius: "0.3rem",
};

const statusChipStyle = (connected) => ({
  fontSize: "0.7rem",
  padding: "2px 10px",
  borderRadius: "999px",
  border: `1px solid ${connected ? "#1E8E3E" : "#1A73E8"}`,
  color: connected ? "#1E8E3E" : "#1A73E8",
  whiteSpace: "nowrap",
});

const DraggableShortcutRow = ({ shortcut, isPinned, onClick, onRemove }) => {
  // No CSS transform applied here — the dragged clone is rendered separately
  // via <DragOverlay>. Translating this row in place let it grow past the
  // Popover's fixed width and made the whole menu scroll sideways and go
  // blank while dragging.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: shortcut.id,
  });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      style={{
        opacity: isDragging ? 0.4 : 1,
        cursor: "grab",
        touchAction: "none",
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        padding: "0.4rem 0",
      }}
    >
      <Box sx={iconBoxStyle}>
        <img src={shortcut.icon} alt="" style={{ width: "16px", height: "16px" }} />
      </Box>
      <Typography fontSize="0.85rem">{shortcut.label}</Typography>
      {isPinned && (
        <Box
          component="img"
          src={removeIcon}
          alt="Remove"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(shortcut.id);
          }}
          sx={{ width: "14px", height: "14px", cursor: "pointer", flexShrink: 0, marginLeft: "auto" }}
        />
      )}
    </Box>
  );
};

const CloudStorageRow = ({ label, icon, connected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "0.75rem",
      padding: "0.4rem 0",
      cursor: "pointer",
    }}
  >
    <Box display="flex" alignItems="center" gap="0.75rem">
      <Box sx={iconBoxStyle}>
        <img src={icon} alt="" style={{ width: "18px", height: "18px" }} />
      </Box>
      <Typography fontSize="0.85rem">{label}</Typography>
    </Box>
    <Box component="span" sx={statusChipStyle(connected)}>
      {connected ? "Connected" : "Connect"}
    </Box>
  </Box>
);

const ChatMoreMenu = ({
  anchorEl,
  open,
  onClose,
  pinnedShortcuts,
  activeDragShortcutId,
  onShortcutAction,
  onUnpinShortcut,
  onOpenCloudStorage,
  isShortcutVisible = () => true,
}) => {
  const [providerStatus, setProviderStatus] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getCloudStorageProviders()
      .then((providers) => {
        if (!cancelled) setProviderStatus(providers || []);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const isConnected = (providerId) =>
    Boolean(
      providerStatus?.find((item) => item?.provider === providerId)?.connected,
    );

  const activeDragShortcut = activeDragShortcutId
    ? getChatShortcutMeta(activeDragShortcutId)
    : null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            width: "300px",
            padding: "1rem",
            borderRadius: "0.25rem",
            marginTop: "0.25rem"
          },
        },
      }}
    >
      <Typography fontSize="0.85rem" fontWeight={600} mb={1.5}>
        Drag & Drop max 4 icons on chat screen for every day use
      </Typography>

      <Typography
        fontSize="0.8rem"
        fontWeight={600}
        color="text.secondary"
        mb={0.5}
      >
        Group Settings
      </Typography>
      {CHAT_SHORTCUTS.filter((shortcut) => isShortcutVisible(shortcut.id)).map((shortcut) => (
        <DraggableShortcutRow
          key={shortcut.id}
          shortcut={shortcut}
          isPinned={pinnedShortcuts?.includes(shortcut.id)}
          onClick={() => {
            onShortcutAction(shortcut.id);
            onClose();
          }}
          onRemove={onUnpinShortcut}
        />
      ))}

      <Divider sx={{ my: 1.5 }} />

      <Typography
        fontSize="0.8rem"
        fontWeight={600}
        color="text.secondary"
        mb={0.5}
      >
        Cloud Storage
      </Typography>
      <CloudStorageRow
        label="Google Drive"
        icon={googleDriveIcon}
        connected={isConnected("google-drive")}
        onClick={() => {
          onOpenCloudStorage("google-drive");
          onClose();
        }}
      />
      <CloudStorageRow
        label="One Drive"
        icon={oneDriveIcon}
        connected={isConnected("onedrive")}
        onClick={() => {
          onOpenCloudStorage("onedrive");
          onClose();
        }}
      />

      <DragOverlay dropAnimation={null}>
        {activeDragShortcut ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              padding: "0.4rem 0.75rem",
              backgroundColor: "#fff",
              border: "1px solid rgba(0, 0, 0, 0.15)",
              borderRadius: "0.3rem",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
              pointerEvents: "none",
            }}
          >
            <Box sx={iconBoxStyle}>
              <img
                src={activeDragShortcut.icon}
                alt=""
                style={{ width: "16px", height: "16px" }}
              />
            </Box>
            <Typography fontSize="0.85rem">{activeDragShortcut.label}</Typography>
          </Box>
        ) : null}
      </DragOverlay>
    </Popover>
  );
};

export default ChatMoreMenu;
